<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps<{
  field: any;
  settings?: any;
}>();

const overlayPanel = ref();

const animalData = computed(() => {
  if (!Array.isArray(props.field) || !props.field || !props.field.length) return [];

  return props.field.map(({ animal_id, animal_name, photo, _id, id, pdf_image_src }: any) => ({
    ANIMAL_NAME: animal_name,
    ANIMAL_PHOTO: photo || pdf_image_src,
    ID: animal_id || _id,
    ID2: id,
  }));
});
const displayedAnimals = computed(() => animalData.value.slice(0, 4));
const hasMoreAnimals = computed(() => animalData.value.length > 4);
const link = computed(() => {
  if (props.settings?.link) return props.settings?.link;

  return props.field?.[0].adopt_user_id ? '/shelter/adoptions/animal/requests/' : '/shelter/animal-profile/';
});

const toggleOverlay = (event: Event) => {
  overlayPanel.value.toggle(event);
};

const closeOverlay = () => {
  overlayPanel.value.hide();
};
</script>

<template>
  <div
    v-if="animalData.length && !settings.noPhoto"
    class="flex w-fit items-center gap-1"
  >
    <RouterLink
      v-for="animal in displayedAnimals"
      :key="animal.ID"
      :to="link + animal.ID"
    >
      <AAvatar
        v-tooltip.top="animal.ANIMAL_NAME"
        class="min-w-10 border-2 border-solid p-1 transition-colors hover:border-brand-400"
        :src="animal.ANIMAL_PHOTO"
      />
    </RouterLink>

    <Button
      v-if="hasMoreAnimals"
      v-tooltip="{
        position: 'top',
        value: $t('common.seeMore'),
        showDelay: 1000,
      }"
      class="af-button flex w-9 justify-center text-xs"
      severity="secondary"
      :label="`+${animalData.length - 4}`"
      @click="toggleOverlay"
    />
  </div>

  <div
    v-else-if="animalData.length && settings.noPhoto"
    class="flex items-center gap-1 whitespace-nowrap"
  >
    <div class="flex w-fit items-center">
      <RouterLink
        v-for="(animal, index) in displayedAnimals"
        :class="[
          'af-link items-center overflow-hidden text-ellipsis whitespace-nowrap px-2 text-center text-xs first:pl-0 last:border-none last:pr-0',
          { 'border-r': index < 3 },
        ]"
        :key="animal.ID.$oid || animal.ID"
        :to="link + (animal.ID.$oid || animal.ID)"
      >
        <p>{{ animal.ID2 }}</p>

        <p class="overflow-hidden text-ellipsis whitespace-nowrap">{{ animal.ANIMAL_NAME }}</p>
      </RouterLink>

      <Button
        v-if="hasMoreAnimals"
        v-tooltip="{
          position: 'top',
          value: $t('common.seeMore'),
          showDelay: 1000,
        }"
        class="af-button flex w-9 justify-center text-xs"
        severity="secondary"
        :label="`+${animalData.length - 4}`"
        @click="toggleOverlay"
      />
    </div>
  </div>

  <span v-else> - </span>

  <!-- Overlay Panel -->
  <OverlayPanel ref="overlayPanel">
    <div class="max-w-80">
      <div class="mb-3 text-sm font-semibold text-gray-700">Animals - {{ animalData.length }}</div>

      <div class="max-h-96 overflow-y-auto">
        <!-- With Photos -->
        <div
          v-if="!settings.noPhoto"
          class="flex flex-wrap gap-2"
        >
          <RouterLink
            v-for="animal in animalData"
            :key="animal.ID"
            :to="link + animal.ID"
            class="flex flex-col items-center gap-1 px-1"
            @click="closeOverlay"
          >
            <AAvatar
              class="min-w-10 border-2 border-solid p-1 transition-colors hover:border-brand-400"
              :src="animal.ANIMAL_PHOTO"
            />
            <p class="af-link text-center text-xs">{{ animal.ANIMAL_NAME }}</p>
          </RouterLink>
        </div>

        <!-- Without Photos -->
        <div
          v-else
          class="flex flex-col gap-1"
        >
          <RouterLink
            v-for="animal in animalData"
            :key="animal.ID.$oid || animal.ID"
            :to="link + (animal.ID.$oid || animal.ID)"
            class="af-link flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors hover:bg-gray-50"
            @click="closeOverlay"
          >
            <p>{{ animal.ID2 }}</p>

            <p class="overflow-hidden text-ellipsis whitespace-nowrap">{{ animal.ANIMAL_NAME }}</p>
          </RouterLink>
        </div>
      </div>
    </div>
  </OverlayPanel>
</template>
